import { z } from "zod";
import toFirestoreSchema from "../utils/toFirestoreSchema";

/**
 * Base fields that all books share
 */
const BaseBookFields = {
    /** When the book was hidden/archived; null means visible */
    archivedAt: z.date().nullable().default(null).describe("When the book was hidden/archived"),
    /** When the book record was created */
    createdAt: z.date().describe("When the book record was created"),
    /** Unique book identifier */
    id: z.string().describe("Unique book identifier"),
};

/**
 * SPECIFIC BOOK SCHEMA
 * Represents a specific book selected for the book club
 * Requires title and author, all other fields optional
 */
export const SpecificBookSchema = z.object({
    ...BaseBookFields,
    /** Amazon URL for referral link */
    amazonUrl: z.string().url().optional().describe("Amazon URL for referral link"),
    /** Book author */
    author: z.string().min(1).describe("Book author"),
    /** URL to cover image */
    coverUrl: z.string().url().optional().describe("URL to cover image"),
    /** Book description/blurb */
    description: z.string().optional().describe("Book description or blurb"),
    /** ISBN (10 or 13 digit) */
    isbn: z.string().optional().describe("ISBN-10 or ISBN-13"),
    /** Book title */
    title: z.string().min(1).describe("Book title"),
    /** Type of book selection */
    type: z.literal("specificBook"),
});

/**
 * AUTHOR FOCUS SCHEMA
 * Admin chooses the author. Members can each choose any book by that author.
 * Requires only author, optionally Amazon URL
 */
export const AuthorFocusSchema = z.object({
    ...BaseBookFields,
    /** Amazon URL for referral link */
    amazonUrl: z.string().url().optional().describe("Amazon URL for referral link"),
    /** Book author */
    author: z.string().min(1).describe("Book author"),
    /** Type of book selection */
    type: z.literal("authorFocus"),
});

/**
 * FREE CHOICE SCHEMA
 * Members are free to each choose any book they like.
 * No book details required. Optional themeTitle for display (e.g. "Members' Choice").
 */
export const FreeChoiceSchema = z.object({
    ...BaseBookFields,
    /** Optional display title for the theme (e.g. "Members' Choice", "Summer Reads") */
    themeTitle: z.string().max(100).optional(),
    /** Type of book selection */
    type: z.literal("freeChoice"),
});

/**
 * TBA SCHEMA
 * Placeholder for a session whose book has not yet been decided.
 * Identity-less — carries no title, author, or other book details.
 * Resolved to a specificBook later via the resolveTbaBook callable.
 */
export const TbaSchema = z.object({
    ...BaseBookFields,
    /** Type of book selection */
    type: z.literal("tba"),
});

/**
 * BOOK SCHEMA
 * Discriminated union of unscheduled book types
 */
export const BookSchema = z.discriminatedUnion("type", [
    SpecificBookSchema,
    AuthorFocusSchema,
    FreeChoiceSchema,
    TbaSchema,
]);

/**
 * Scheduled book fields that extend unscheduled books
 */
const ScheduledBookFields = {
    /**
     * TBA book id this document replaced via mergeTbaIntoBook.
     * null when not created by a TBA merge. Cleared on unschedule.
     */
    mergedFromTbaId: z
        .string()
        .nullable()
        .default(null)
        .describe("TBA book id this document replaced via merge, if any"),
    /** Event month (1-12, when the book club event is scheduled) */
    month: z.number().int().min(1).max(12).nullable().default(null).describe("Event month (1-12)"),
    /** Total number of ratings for the book */
    ratingCount: z.number().int().min(0).default(0),
    /** Sum of all ratings for the book */
    ratingSum: z.number().int().min(0).default(0),
    /** Total number of attendees for the book */
    totalAttendees: z.number().int().min(0).default(0),
    /** Total number of bookings for the book */
    totalBookings: z.number().int().min(0).default(0),
    /** Event year (when the book club event is scheduled) */
    year: z.number().int().nullable().default(null).describe("Event year"),
};

/**
 * SCHEDULED SPECIFIC BOOK SCHEMA
 */
export const ScheduledSpecificBookSchema = SpecificBookSchema.extend(ScheduledBookFields);

/**
 * SCHEDULED AUTHOR FOCUS SCHEMA
 */
const ScheduledAuthorFocusSchema = AuthorFocusSchema.extend(ScheduledBookFields);

/**
 * SCHEDULED FREE CHOICE SCHEMA
 */
const ScheduledFreeChoiceSchema = FreeChoiceSchema.extend(ScheduledBookFields);

/**
 * SCHEDULED TBA SCHEMA
 */
const ScheduledTbaSchema = TbaSchema.extend(ScheduledBookFields);

/**
 * SCHEDULED BOOK SCHEMA
 * Discriminated union of scheduled book types
 * Represents a book that has been selected for a month and has been scheduled for a session
 */
export const ScheduledBookSchema = z.discriminatedUnion("type", [
    ScheduledSpecificBookSchema,
    ScheduledAuthorFocusSchema,
    ScheduledFreeChoiceSchema,
    ScheduledTbaSchema,
]);

/**
 * BOOK STATS SCHEMA
 * Mutable statistics extracted from ScheduledBook
 * Used for separate caching of frequently-changing stats
 * Note: Cannot use .pick() on discriminated union, so we define it manually
 */
export const BookStatsSchema = z.object({
    id: z.string(),
    ratingCount: z.number().int().min(0).default(0),
    ratingSum: z.number().int().min(0).default(0),
    totalAttendees: z.number().int().min(0).default(0),
    totalBookings: z.number().int().min(0).default(0),
});

/**
 * Convert each book schema variant to Firestore Record schema
 */
const SpecificBookRecordSchema = toFirestoreSchema(SpecificBookSchema);
const AuthorFocusRecordSchema = toFirestoreSchema(AuthorFocusSchema);
const FreeChoiceRecordSchema = toFirestoreSchema(FreeChoiceSchema);
const TbaRecordSchema = toFirestoreSchema(TbaSchema);

export const ScheduledSpecificBookRecordSchema = toFirestoreSchema(ScheduledSpecificBookSchema);
const ScheduledAuthorFocusRecordSchema = toFirestoreSchema(ScheduledAuthorFocusSchema);
const ScheduledFreeChoiceRecordSchema = toFirestoreSchema(ScheduledFreeChoiceSchema);
const ScheduledTbaRecordSchema = toFirestoreSchema(ScheduledTbaSchema);

/**
 * BOOK RECORD SCHEMA
 * Firestore version of BookSchema (with Timestamps instead of Dates)
 */
export const BookRecordSchema = z.discriminatedUnion("type", [
    SpecificBookRecordSchema,
    AuthorFocusRecordSchema,
    FreeChoiceRecordSchema,
    TbaRecordSchema,
]);

/**
 * SCHEDULED BOOK RECORD SCHEMA
 * Firestore version of ScheduledBookSchema (with Timestamps instead of Dates)
 */
export const ScheduledBookRecordSchema = z.discriminatedUnion("type", [
    ScheduledSpecificBookRecordSchema,
    ScheduledAuthorFocusRecordSchema,
    ScheduledFreeChoiceRecordSchema,
    ScheduledTbaRecordSchema,
]);

// Type export
export type Book = z.infer<typeof BookSchema>;
export type BookType = Book["type"];
export type SpecificBook = Extract<Book, { type: "specificBook" }>;
export type AuthorFocusBook = Extract<Book, { type: "authorFocus" }>;
export type FreeChoiceBook = Extract<Book, { type: "freeChoice" }>;
export type TbaBook = Extract<Book, { type: "tba" }>;
export type BookRecord = z.infer<typeof BookRecordSchema>;
export type ScheduledBook = z.infer<typeof ScheduledBookSchema>;
export type ScheduledBookRecord = z.infer<typeof ScheduledBookRecordSchema>;
export type BookStats = z.infer<typeof BookStatsSchema>;
